import React, { useEffect, useState } from 'react';
import { Card, Button, Spinner } from 'react-bootstrap';
import { GetJson, PutJson } from '@awp/helpers/fetchReact';
import { REST_API, FORM_MODE, EnabledFeature } from '@awp/constants';
import { toast } from 'react-toastify';
import CodeTableViewer from './CodeTableViewer';
import CodeTableEditor from './CodeTableEditor';
import CodeTabelService from './CodeTabelService';
import { CodeTableAdminProps, CodeTableRow } from '../Interfaces/CodeTableInterfaces';

const CodeTableAdmin: React.FC<CodeTableAdminProps> = ({
  observationTypeId,
  mode,
  initialDto,
  setInitialDto,
  isAvalancheObs,
  saveCodeTable,
  setSaveCodeTable,
  errors,
  setErrors,
  props,
}) => {
  const [tableData, setTableData] = useState<CodeTableRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const codeTableService = new CodeTabelService();

  useEffect(() => {
    if (mode !== FORM_MODE.CREATE && observationTypeId) {
      setLoading(true);
      GetJson(`${REST_API.CODE_TABLE}${observationTypeId}`)
        .then((res) => res.json())
        .then((data) => {
          setTableData(data || []);
          initialDto['codetable'] = data || [];
          setInitialDto(initialDto);
          props.setFieldValue('codetable', data);
        })
        .catch(() => {
          setTableData([]);
        })
        .finally(() => setLoading(false));
    }
  }, [observationTypeId]);

  useEffect(() => {
    if (saveCodeTable) {
      setSaveCodeTable(false);
      handleSave();
    }
  }, [saveCodeTable]);

  useEffect(() => {
    if (mode === FORM_MODE.CREATE) {
      setTableData([]);
      initialDto['codetable'] = [];
      setInitialDto(initialDto);
    }
  }, [mode]);

  const handleValidation = (data: CodeTableRow[] | null): boolean => {
    const validationErrors = codeTableService.validate(data ?? [], isAvalancheObs);
    setErrors(validationErrors);
    props.setFieldValue('codetable', data);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSaveComplete = (updatedData: CodeTableRow[]) => {
    setTableData(updatedData);
    initialDto['codetable'] = updatedData || [];
    setInitialDto(initialDto);
    props.setFieldValue('codetable', updatedData);
  };

  const handleSave = async () => {
    setLoading(true);
    if (!observationTypeId) {
      return;
    }
    try {
      if (tableData && tableData.some((x) => x.DisplayOrder == null)) {
        toast.warning('Consider assigning a display order to all rows in the code table for consistent sorting.', {
          autoClose: 5000,
        });
      }

      const payload = tableData?.map((r) => ({
        Id: typeof r.Id === 'string' && r.Id.startsWith('temp-') ? null : r.Id,
        EndTimestamp: r.EndTimestamp,
        ObservedCharacterValue: r.ObservedCharacterValue,
        DisplayedMeaning: r.DisplayedMeaning,
        DisplayOrder: r.DisplayOrder ?? null,
        DisplayedHint: r.DisplayedHint ?? null,
        ObservationTypeId: observationTypeId,
        ConcurrencyControlNumber: r.ConcurrencyControlNumber ?? 0,
      }));

      const response = await PutJson(`${REST_API.CODE_TABLE}${observationTypeId}`, payload);
      const result = await response.json();

      if (result.Errors && result.Errors.length > 0) {
        toast.error(result.Errors.join(' '), { autoClose: 5000 });
      } else {
        handleSaveComplete(result);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to save code table data.', { autoClose: 5000 });
    } finally {
      setLoading(false);
    }
  };

  const handleAddRow = () => {
    const newRow: CodeTableRow = {
      Id: `temp-${Date.now()}`,
      ObservedCharacterValue: '',
      DisplayedMeaning: '',
      DisplayedHint: '',
      EndTimestamp: null,
      DisplayOrder: null,
      ObservationTypeId: observationTypeId,
      ConcurrencyControlNumber: 0,
    };
    const updated = [...(tableData ?? []), newRow];
    setTableData(updated);
    initialDto['codetable'] = updated;
    setInitialDto(initialDto);
  };

  if ((observationTypeId && loading) || (observationTypeId && tableData == null)) {
    return (
      <div className="text-center">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <>
      {EnabledFeature.CodeTable && (
        <>
          {mode !== FORM_MODE.VIEW && (tableData == null || (tableData != null && tableData.length === 0)) && (
            <Button variant="primary" onClick={handleAddRow}>
              Create Code Table
            </Button>
          )}

          {tableData != null && tableData.length > 0 ? (
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <h4>Code Table</h4>
                </div>
              </Card.Header>
              <Card.Body>
                {mode === FORM_MODE.VIEW ? (
                  <CodeTableViewer
                    observationTypeId={observationTypeId}
                    initialData={tableData}
                    codeTableService={codeTableService}
                  />
                ) : (
                  <CodeTableEditor
                    id="codetable"
                    name="codetable"
                    observationTypeId={observationTypeId}
                    codeTableData={tableData}
                    setCodeTableData={setTableData}
                    isAvalancheObs={isAvalancheObs}
                    errors={errors}
                    setErrors={setErrors}
                    props={props}
                    codeTableService={codeTableService}
                    validateTable={handleValidation}
                  />
                )}
              </Card.Body>
            </Card>
          ) : null}
        </>
      )}
    </>
  );
};

export default CodeTableAdmin;
